import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NotifierService } from 'angular-notifier';
import { FlatpickrDefaultsInterface } from 'angularx-flatpickr';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
    BankAccountActionConfirmationConfig,
    BankAccountActionDateRangeSelection,
    BankAccountActionTerminalOption
} from './bank-account-action-confirmation-modal.model';

type TerminalMode = 'all' | 'specific';

@Component({
    selector: 'app-bank-account-action-confirmation-modal',
    templateUrl: './bank-account-action-confirmation-modal.component.html',
    styleUrls: ['./bank-account-action-confirmation-modal.component.scss'],
    standalone: false
})
export class BankAccountActionConfirmationModalComponent implements OnInit, OnDestroy {

    @Input() config!: BankAccountActionConfirmationConfig;

    confirmationForm: UntypedFormGroup;
    dateRangeForm: UntypedFormGroup | null = null;
    terminalForm: UntypedFormGroup | null = null;
    flatpickrFromOptions: FlatpickrDefaultsInterface = {};
    flatpickrToOptions: FlatpickrDefaultsInterface = {};

    // Terminal selection state
    terminalOptions: BankAccountActionTerminalOption[] = [];
    isLoadingTerminals = false;
    terminalsLoaded = false;
    terminalLoadError = false;

    isProcessing = false;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        public activeModal: NgbActiveModal,
        public _translate: TranslateService,
        private _formBuilder: FormBuilder,
        private _notifierService: NotifierService
    ) {
        this.confirmationForm = this._formBuilder.group({
            confirmationInput: [null, [Validators.required]]
        });
    }

    ngOnInit(): void {
        if (this.config?.dateRange) {
            const maxDate = this.config.dateRange.maxDate || new Date();

            this.flatpickrFromOptions = {
                maxDate,
                dateFormat: 'Y-m-d',
                allowInput: false
            };
            this.flatpickrToOptions = {
                maxDate,
                dateFormat: 'Y-m-d',
                allowInput: false
            };

            // Both controls are optional — the action can run without dates.
            this.dateRangeForm = this._formBuilder.group({
                fromDate: [null],
                toDate: [null]
            });
        }

        if (this.config?.terminalSelection) {
            // Defaults to "all terminals" so the action keeps its current behavior
            // unless the user explicitly narrows it down.
            this.terminalForm = this._formBuilder.group({
                mode: ['all' as TerminalMode],
                terminalIds: [[]]
            });

            // Load terminals as soon as the modal opens so the list is ready by the
            // time the user switches to "specific terminals".
            this.loadTerminals();
        }
    }

    get terminalMode(): TerminalMode {
        return this.terminalForm?.get('mode')?.value ?? 'all';
    }

    /** Switching back to "all" clears any prior specific selection. */
    onTerminalModeChange(mode: TerminalMode): void {
        if (mode === 'all') {
            this.terminalForm?.get('terminalIds')?.setValue([]);
        }
    }

    private loadTerminals(): void {
        if (!this.config?.terminalSelection || this.terminalsLoaded || this.isLoadingTerminals) {
            return;
        }

        this.isLoadingTerminals = true;
        this.terminalLoadError = false;

        this.config.terminalSelection.loadTerminals()
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoadingTerminals = false;
                })
            )
            .subscribe({
                next: (terminals) => {
                    this.terminalOptions = terminals || [];
                    this.terminalsLoaded = true;
                },
                error: () => {
                    this.terminalLoadError = true;
                }
            });
    }

    /** No terminals are linked to this bank account. */
    get hasNoTerminals(): boolean {
        return this.terminalsLoaded && this.terminalOptions.length === 0;
    }

    /** Disable the selector while loading or when there is nothing to pick. */
    get isTerminalSelectDisabled(): boolean {
        return this.isLoadingTerminals || this.hasNoTerminals;
    }

    /**
     * True when the terminal section is in an invalid state, i.e. the user picked
     * "specific terminals" but has no valid selection (none chosen, or none exist).
     */
    get terminalSelectionError(): boolean {
        if (!this.terminalForm || this.terminalMode !== 'specific') {
            return false;
        }
        const selected: string[] = this.terminalForm.get('terminalIds')?.value || [];
        return selected.length === 0;
    }

    /**
     * Wide (two-column) layout is used only when the action carries extra scope
     * controls (date range and/or terminal selection). Simple confirmations stay
     * single-column.
     */
    get isWideLayout(): boolean {
        return !!(this.config?.dateRange || this.config?.terminalSelection);
    }

    get expectedConfirmationText(): string {
        return this.config?.confirmationText ?? '';
    }

    get isConfirmationValid(): boolean {
        return this.confirmationForm.get('confirmationInput')?.value === this.expectedConfirmationText;
    }

    get showValidationError(): boolean {
        const confirmationInput = this.confirmationForm.get('confirmationInput');
        return confirmationInput?.touched && !this.isConfirmationValid;
    }

    /**
     * Returns a validation error key when a partial range was entered
     * (only one of from/to) or when from is after to.
     */
    get dateRangeError(): string | null {
        if (!this.dateRangeForm) return null;
        const from = this.dateRangeForm.value.fromDate;
        const to = this.dateRangeForm.value.toDate;

        if (!from && !to) return null;
        if ((from && !to) || (!from && to)) {
            return 'Please select both From Date and To Date, or leave both empty.';
        }
        if (new Date(from) > new Date(to)) {
            return 'From Date must be on or before To Date.';
        }
        return null;
    }

    get isReadyToConfirm(): boolean {
        return this.isConfirmationValid && !this.dateRangeError && !this.terminalSelectionError;
    }

    /** Quick action: clear both dates so the API is called without query params. */
    clearDateRange(): void {
        this.dateRangeForm?.patchValue({ fromDate: null, toDate: null });
    }

    /** Clear a single date control (matches the insights dashboard rest-icon behavior). */
    clearDateControl(controlName: 'fromDate' | 'toDate'): void {
        this.dateRangeForm?.get(controlName)?.setValue(null);
    }

    onConfirm(): void {
        if (!this.isReadyToConfirm || this.isProcessing) {
            return;
        }

        this.isProcessing = true;

        // Only scope to specific terminals when the user explicitly chose them.
        const terminalIds: string[] | undefined =
            this.terminalForm && this.terminalMode === 'specific'
                ? this.terminalForm.value.terminalIds || []
                : undefined;

        const selection: BankAccountActionDateRangeSelection | undefined =
            this.dateRangeForm || terminalIds
                ? {
                    fromDate: this.dateRangeForm?.value.fromDate || undefined,
                    toDate: this.dateRangeForm?.value.toDate || undefined,
                    terminalIds: terminalIds?.length ? terminalIds : undefined
                }
                : undefined;

        this.config.onConfirm(selection)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isProcessing = false;
                })
            )
            .subscribe({
                next: (response) => {
                    this.config.onSuccess?.(response);
                    this._notifierService.notify('success', this.config.successMessage);
                    this.activeModal.close('confirmed');
                },
                error: () => { }
            });
    }

    onCancel(): void {
        this.activeModal.dismiss('cancel');
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
